import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWrapper } from '../../shared/api/fetchWrapper';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(!!localStorage.getItem('token'));
  const [authSessionKey, setAuthSessionKey] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const checkToken = async () => {
      const checkSessionProcessId = process.env.REACT_APP_CHECK_SESSION_PROCESS;
      const token = localStorage.getItem('token');
      
      if (!token || typeof token !== 'string') {
        setIsCheckingSession(false);
        handleLogout();
        return;
      }

      try {
        const response = await fetchWrapper(checkSessionProcessId, { token });
        if (!response || typeof response !== 'object') {
          setIsCheckingSession(false);
          handleLogout();
          return;
        }

        if (response.member?.token === token) {
          setUser(response.member);
        } else {
          handleLogout();
        }
        
        if (response.token_return === false) {
          navigate('/login');
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        handleLogout();
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkToken();

    const tokenCheckInterval = setInterval(() => {
      checkToken();
    }, 300000); // 5 dakikada bir token'ı kontrol et

    return () => clearInterval(tokenCheckInterval); // Component unmount olduğunda interval'ı temizle
  }, [navigate]);

  const login = async (credentials) => {
    if (!credentials?.email || !credentials?.password) {
      setLoginError(true);
      return { responsecode: 1, message: 'Geçersiz kimlik bilgileri' };
    }

    setIsLoggingIn(true); // Login başladığında hemen true yap
    setLoginError(false); // Login başladığında hata durumunu sıfırla
    const loginProcessId = process.env.REACT_APP_LOGIN_PROCESS;
    const userData = await fetchWrapper(loginProcessId, credentials);
    if (userData?.responsecode === 0 && userData?.token) {
      localStorage.setItem('token', userData.token);
      setUser(userData.member);
      setLoginError(false);
      navigate('/');
    } else {
      setIsLoggingIn(false); // Login başarısızsa false yap
      setLoginError(true); // Login başarısızsa hata durumunu true yap
    }
    return userData;
  };

  const handleLogout = () => {
    setUser(null);
    setIsLoggingIn(false);
    setLoginError(false);
    localStorage.removeItem('token');
    setAuthSessionKey((prev) => prev + 1);
    navigate('/login');
  };

  const logout = async () => {
    const logoutProcessId = process.env.REACT_APP_LOGOUT_PROCESS;
    const token = localStorage.getItem('token');

    try {
      const logoutResult = await fetchWrapper(logoutProcessId, { token });
      if (logoutResult.responsecode === 0) {
        handleLogout(); // Başarılıysa logout işlemini yap
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, token, isAuthenticated: !!user, isLoggingIn, setIsLoggingIn, loginError, setLoginError, isCheckingSession }}>
      <React.Fragment key={authSessionKey}>
        {children}
      </React.Fragment>
    </AuthContext.Provider>
  );
};
